import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../store/useStore";
import HeaderStrip from "./Header/HeaderStrip";
import SearchBar from "./Header/SearchBar";
import LocationSelector from "./Header/LocationSelector";
import CartButton from "./Header/CartButton";
import WishlistNavButton from "./Header/WishlistNavButton";
import UserProfile from "./Header/UserProfile";
import CategoryDropdown from "./Header/CategoryDropdown";
import useBodyScrollLock from "../utils/useBodyScrollLock";
import {
  FaBoxOpen,
  FaHeart,
  FaHome,
  FaInfoCircle,
  FaLeaf,
  FaPhoneAlt,
  FaShoppingCart,
  FaTruck,
  FaUser,
} from "react-icons/fa";

const Header = () => {
  const {
    searchQuery,
    setSearchQuery,
    setSelectedCategory,
    categories,
    fetchCategories,
    fetchCart,
    fetchWishlist,
    cart_count,
    isAuthenticated,
    user,
    logout,
    openAuthModal,
  } = useStore();

  const [selectedLocation, setSelectedLocation] = useState("Pune");
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!categories.length) fetchCategories();
  }, [categories.length, fetchCategories]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
      if (typeof fetchWishlist === 'function') fetchWishlist();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Close drawer on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target))
        setMobileOpen(false);
    };
    if (mobileOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  useBodyScrollLock(mobileOpen);

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };
  const handleCategoryClick = (id) => {
    setSelectedCategory(id);
    go(`/products/${id}`);
  };

  const initials = user?.name
    ? user.name.slice(0, 2).toUpperCase()
    : user?.mobile
      ? user.mobile.slice(-2)
      : "U";

  return (
    <>
      <header className="sticky top-0 z-[60] w-full border-b border-gray-100 bg-white shadow-sm">
        <HeaderStrip />

        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 py-2 lg:gap-6">
            {/* Logo */}
            <div
              className="flex shrink-0 cursor-pointer flex-col items-center"
              onClick={() => go("/")}
            >
              <img
                src="/k_kisanlogo.png"
                alt="KKisan"
                className="h-12 w-auto sm:h-14 lg:h-16"
              />
              <span className="mt-0.5 text-[10px] font-bold leading-none text-primary-700 sm:text-xs">
                Kisan Daily Fresh
              </span>
            </div>

            {/* Search — grows to fill space */}
            <div className="flex-1">
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </div>

            {/* Desktop nav */}
            <div className="hidden items-center gap-3 lg:flex">
              <CategoryDropdown
                categories={categories}
                onCategoryClick={handleCategoryClick}
              />
              <LocationSelector
                value={selectedLocation}
                onChange={setSelectedLocation}
              />
              <WishlistNavButton className="h-11 w-11 p-2.5" />
              <CartButton
                count={cart_count}
                onClick={() => go("/cart")}
                className="h-11 w-11 p-2.5"
              />
              <UserProfile
                isAuthenticated={isAuthenticated}
                user={user}
                onLogout={() => {
                  logout();
                  go("/");
                }}
                onLogin={openAuthModal}
              />
            </div>

            {/* Mobile right icons */}
            <div className="flex items-center gap-1.5 lg:hidden">
              <WishlistNavButton className="h-10 w-10 p-2" />
              <CartButton
                count={cart_count}
                onClick={() => go("/cart")}
                className="h-10 w-10 p-2"
              />
              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen((p) => !p)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-700 transition hover:bg-primary-50 hover:text-primary-600"
                aria-label="Menu"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      mobileOpen
                        ? "M6 18L18 6M6 6l12 12"
                        : "M4 6h16M4 12h16M4 18h16"
                    }
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] overflow-hidden bg-black/40 backdrop-blur-sm lg:hidden">
          {/* Drawer panel */}
          <div
            ref={drawerRef}
            className="absolute right-0 top-0 flex h-full w-[300px] max-w-[85vw] flex-col overflow-hidden bg-white shadow-2xl"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-4">
              <div className="flex flex-col items-center">
                <img
                  src="/k_kisanlogo.png"
                  alt="KKisan"
                  className="h-10 w-auto brightness-0 invert"
                />
                <span className="mt-1 text-[10px] font-bold leading-none text-white">
                  Kisan Daily Fresh
                </span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* User info strip */}
            <div className="border-b border-gray-100 bg-primary-50 px-5 py-3">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-extrabold text-white">
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      {user?.name || "My Account"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.mobile || user?.email || ""}
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    openAuthModal();
                    setMobileOpen(false);
                  }}
                  className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white shadow hover:bg-primary-700"
                >
                  Login / Sign Up
                </button>
              )}
            </div>

            {/* Scrollable content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Quick links */}
              <div className="px-4 py-3">
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Quick Links
                </p>
                {[
                  { label: "Home", Icon: FaHome, path: "/" },
                  { label: "All Products", Icon: FaShoppingCart, path: "/products" },
                  { label: "My Orders", Icon: FaBoxOpen, path: "/account/orders" },
                  { label: "Track Order", Icon: FaTruck, path: "/account/track" },
                  { label: "Wishlist", Icon: FaHeart, path: "/account/wishlist" },
                  { label: "My Account", Icon: FaUser, path: "/account/profile" },
                  { label: "About Us", Icon: FaInfoCircle, path: "/about" },
                  { label: "Contact Us", Icon: FaPhoneAlt, path: "/contact" },
                ].map(({ label, Icon, path }) => (
                  <button
                    key={label}
                    onClick={() => go(path)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-gray-700 transition hover:bg-primary-50 hover:text-primary-700"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-primary-600" aria-hidden="true" />
                    {label}
                    <svg
                      className="ml-auto h-4 w-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                ))}
              </div>

              {/* Categories */}
              {categories.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-3">
                  <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Categories
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.category_id}
                        onClick={() => handleCategoryClick(cat.category_id)}
                        className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                      >
                        {cat.image ? (
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="h-5 w-5 object-contain"
                          />
                        ) : (
                          <FaLeaf className="h-4 w-4 text-primary-600" aria-hidden="true" />
                        )}
                        <span className="truncate">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="border-t border-gray-100 px-4 py-3">
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Delivery Location
                </p>
                <LocationSelector
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                />
              </div>
            </div>

            {/* Drawer footer */}
            <div className="border-t border-gray-100 px-4 py-3">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    logout();
                    go("/");
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 py-2.5 text-sm font-bold text-red-500 hover:bg-red-100"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              ) : (
                <p className="text-center text-xs text-gray-400">
                  © 2024 KKisan Fresh
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
